import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import styles from './css/index.module.scss';
import PageTitle from '../../components/PageTitle';
import sendRequest from '../../helpers/sendRequest';
import Api from '../../Api';
import IconButton from '../../components/IconButton';
import CancelEditingIcon from '../../assets/formIcons/cancelEditing.svg';
import EmptyHeight from '../../components/EmptyHeight';
import { getActionText, getPaymentTypeText, getStatusText } from '../Payments/constants';

function ViewSinglePayment() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('id');

  const [gettingLoading, setGettingLoading] = useState(true);
  const [gettingError, setGettingError] = useState('');
  const [singlePayment, setSinglePayment] = useState(null);

  const onCancelClick = () => {
    navigate('/payments/view');
  };

  const initData = async () => {
    const data = await sendRequest({
      request: Api.getPayment,
      payload: { id: paymentId },
      warnErrorText: 'while getting single payment',
      setLoading: setGettingLoading,
    });

    if (data?.status !== 'ok') {
      setGettingError(typeof data.message === 'string' ? data.message : 'Ошибка');
    }

    setSinglePayment(data.payment);
  };

  useEffect(() => {
    initData().then();
  }, []);

  return (
    <div className={styles.wrapper}>
      <PageTitle
        text="Просмотр платежа"
        RightComponent={(
          <IconButton
            svgPath={CancelEditingIcon}
            tooltipText="Отменить"
            onClick={onCancelClick}
          />
        )}
      />
      {gettingLoading
        ? <p className={styles.infoText}>Загрузка</p>
        : gettingError
          ? <p className={styles.infoText}>{gettingError}</p>
          : singlePayment ? (
            <div className={styles.container}>
              <div className={styles.column}>
                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Действие</p>
                  </div>
                  <p className={styles.text}>
                    {getActionText(singlePayment.action_to_pay_for)}
                  </p>
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Сумма</p>
                  </div>
                  <p className={styles.text}>
                    {singlePayment.value} {singlePayment.currency?.toUpperCase() || 'USD'}
                  </p>
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Тип оплаты</p>
                  </div>
                  <p className={styles.text}>
                    {getPaymentTypeText(singlePayment.payment_type)}
                  </p>
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Статус</p>
                  </div>
                  <p className={styles.text}>
                    {getStatusText(singlePayment.status)}
                  </p>
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Дата создания</p>
                    <p className={styles.infoTitle}>
                      {singlePayment.createdAt ? moment(singlePayment.createdAt).format('DD.MM.YYYY') : '—'}
                    </p>
                  </div>
                  <p className={styles.text}>
                    {singlePayment.createdAt ? moment(singlePayment.createdAt).format('DD.MM.YYYY HH:mm:ss') : '—'}
                  </p>
                </div>

                <div className={styles.infoBlock}>
                  <div className={styles.infoHeader}>
                    <p className={styles.infoTitle}>Дата обновления</p>
                    <p className={styles.infoTitle}>
                      {singlePayment.updatedAt ? moment(singlePayment.updatedAt).format('DD.MM.YYYY') : '—'}
                    </p>
                  </div>
                  <p className={styles.text}>
                    {singlePayment.updatedAt ? moment(singlePayment.updatedAt).format('DD.MM.YYYY HH:mm:ss') : '—'}
                  </p>
                </div>
              </div>

              <div className={styles.column}>
                {singlePayment.user_id && (
                  <div className={styles.infoBlock}>
                    <div className={styles.infoHeader}>
                      <p className={styles.infoTitle}>Пользователь</p>
                    </div>
                    <p className={styles.text}>
                      {singlePayment.user_id.display_name || singlePayment.user_id.email || '—'}
                    </p>
                    {singlePayment.user_id.email && (
                      <>
                        <EmptyHeight height={16} />
                        <p className={styles.textGray}>
                          {singlePayment.user_id.email}
                        </p>
                      </>
                    )}
                    {singlePayment.user_id._id && (
                      <>
                        <EmptyHeight height={16} />
                        <p className={styles.textGray}>
                          ID: {singlePayment.user_id._id}
                        </p>
                      </>
                    )}
                  </div>
                )}

                {singlePayment.device_id && (
                  <div className={styles.infoBlock}>
                    <div className={styles.infoHeader}>
                      <p className={styles.infoTitle}>Устройство</p>
                    </div>
                    <p className={styles.text}>
                      {singlePayment.device_id.device_unique_id || '—'}
                    </p>
                    {singlePayment.device_id.platform && (
                      <>
                        <EmptyHeight height={16} />
                        <p className={styles.textGray}>
                          Платформа: {singlePayment.device_id.platform}
                        </p>
                      </>
                    )}
                    {singlePayment.device_id.app_version && (
                      <>
                        <EmptyHeight height={16} />
                        <p className={styles.textGray}>
                          Версия приложения: {singlePayment.device_id.app_version}
                        </p>
                      </>
                    )}
                  </div>
                )}

                {singlePayment.action_to_pay_for === 'organization_promotion' && singlePayment.payload_details && (
                  <div className={styles.infoBlock}>
                    <div className={styles.infoHeader}>
                      <p className={styles.infoTitle}>Детали (продвижение)</p>
                    </div>
                    {singlePayment.payload_details.organization_name && (
                      <p className={styles.text}>Предложение: {singlePayment.payload_details.organization_name}</p>
                    )}
                    {singlePayment.payload_details.city_name && (
                      <p className={styles.textGray}>Город: {singlePayment.payload_details.city_name}</p>
                    )}
                    {singlePayment.payload_details.category_name && (
                      <p className={styles.textGray}>Категория: {singlePayment.payload_details.category_name}</p>
                    )}
                    {singlePayment.payload_details.sub_category_name && (
                      <p className={styles.textGray}>Подкатегория: {singlePayment.payload_details.sub_category_name}</p>
                    )}
                  </div>
                )}

                {singlePayment.error_text && (
                  <div className={styles.infoBlock}>
                    <div className={styles.infoHeader}>
                      <p className={styles.infoTitle}>Текст ошибки</p>
                    </div>
                    <pre className={styles.text} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#E53E3E' }}>
                      {singlePayment.error_text}
                    </pre>
                  </div>
                )}

                {singlePayment.info_text && (
                  <div className={styles.infoBlock}>
                    <div className={styles.infoHeader}>
                      <p className={styles.infoTitle}>Информация</p>
                    </div>
                    <pre className={styles.text} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {singlePayment.info_text}
                    </pre>
                  </div>
                )}

                {singlePayment.payload && Object.keys(singlePayment.payload).length > 0 && (
                  <div className={`${styles.infoBlock} ${styles.infoBlockWide}`}>
                    <div className={styles.infoHeader}>
                      <p className={styles.infoTitle}>Payload</p>
                    </div>
                    <pre className={styles.text} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(singlePayment.payload, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          ) : null}
    </div>
  );
}

export default ViewSinglePayment;
